<template>
  <div
    class="flex-shrink-0 bl-input-component bg-pure-white"
    :class="{
      'bl-input-component-active': opened,
      'bl-input-component-dense': input.dense
    }"
    @click="opened = true"
  >
    <label class="bl-input-label" :for="id" v-html="activeLabel"></label>
    <BlBubbleCount
      v-if="!input.dense && filteredCount > 0"
      :count="filteredCount"
      :minified="true"
    />
    <input
      type="text"
      class="bl-input"
      :id="id"
      readonly
      @focusin="opened = true"
      @focusout="opened = false"
    />
    <div
      class="bl-input-border"
      :class="{ 'bl-input-border-dense': input.dense }"
    ></div>
    <BlIcon
      class="transition-all duration-300 bl-input-icon"
      :class="{ 'bl-input-icon-dense': input.dense }"
      icon="chevron-down"
      :style="opened ? 'rotate: 180deg' : ''"
    />

    <div v-if="opened" class="bl-input-dropdown" :data-id="id">
      <div class="bl-input-dropdown-header" :data-id="id">
        <BlTextSubtitle2 :text="title" :data-id="id" />
        <BlInputButton
          :label="defaultValue ? 'Reset to default' : 'Clear all'"
          type="plain"
          :data-id="id"
          @clicked="onClearAll"
        />
        <div class="bl-input-dropdown-menu">
          <div class="bl-input-dropdown-tabs" :data-id="id">
            <div
              v-if="menuTabs.length > 0"
              v-for="(data, i) in menuTabs"
              :key="i"
              class="bl-input-dropdown-tab"
              :class="i === tabIndex ? 'bl-input-dropdown-tab-active' : ''"
              @click="onChangeTab(data)"
              :data-id="id"
            >
              <BlTextSubtitle2
                :text="data.name"
                class="font-normal whitespace-no-wrap pointer-events-none"
              />
              <BlBubbleCount
                v-if="getTabFilteredCount(data, i) > 0"
                :type="data.name === tab.name ? 'primary' : 'secondary'"
                minified
                class="pointer-events-none"
                :count="getTabFilteredCount(data, i)"
              />
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="tab.kind === 'date'"
        class="gap-2 px-4 py-2 bl-input-dropdown-body"
        :data-id="id"
      >
        <div
          class="bl-input-dropdown-option"
          :data-id="id"
          @click="date = 'last_week'"
        >
          <BlInputRadio
            :checked="date === 'last_week'"
            :data-id="id"
            name="date"
          />
          <span class="bl-input-dropdown-option-name" :data-id="id">
            Last 7 days
          </span>
        </div>
        <div
          class="bl-input-dropdown-option"
          :data-id="id"
          @click="date = 'last_month'"
        >
          <BlInputRadio
            :checked="date === 'last_month'"
            :data-id="id"
            name="date"
          />
          <span class="bl-input-dropdown-option-name" :data-id="id">
            Last 30 days
          </span>
        </div>
        <div
          class="bl-input-dropdown-option"
          :data-id="id"
          @click="date = 'last_year'"
        >
          <BlInputRadio
            :checked="date === 'last_year'"
            :data-id="id"
            name="date"
          />
          <span class="bl-input-dropdown-option-name" :data-id="id">
            Last 365 days
          </span>
        </div>
        <div
          v-if="tab.all_time"
          class="bl-input-dropdown-option"
          :data-id="id"
          @click="date = 'all_time'"
        >
          <BlInputRadio
            :checked="date === 'all_time'"
            :data-id="id"
            name="date"
          />
          <span class="bl-input-dropdown-option-name" :data-id="id">
            All time
          </span>
        </div>
        <div
          class="bl-input-dropdown-option"
          :data-id="id"
          @click="date = 'custom'"
        >
          <BlInputRadio
            :checked="
              date !== 'last_week' &&
                date !== 'last_month' &&
                date !== 'last_year' &&
                date !== 'all_time'
            "
            :data-id="id"
            name="date"
          />
          <span class="bl-input-dropdown-option-name" :data-id="id">
            Custom
          </span>
        </div>
        <div class="bl-input-dropdown-date-wrapper" :data-id="id">
          <BlInputDate
            class="bl-input-dropdown-date bl-input-dropdown-sub-input"
            :input="startDateInput"
            :data-id="id"
            @changed="inputHandler"
            :disabledManualInput="false"
          />
          <div class="bl-input-dropdown-date-divider"></div>
          <BlInputDate
            class="bl-input-dropdown-date bl-input-dropdown-sub-input"
            :input="endDateInput"
            :data-id="id"
            @changed="inputHandler"
            :disabledManualInput="false"
          />
        </div>
      </div>

      <div
        v-if="tab.kind === 'date-custom'"
        class="gap-2 px-4 py-2 bl-input-dropdown-body"
        :data-id="id"
      >
        <div class="bl-input-dropdown-date-wrapper" :data-id="id">
          <BlInputDate
            class="bl-input-dropdown-date bl-input-dropdown-sub-input"
            :input="startDateInput"
            :data-id="id"
            @changed="inputHandler"
            disabledManualInput
          />
          <div class="bl-input-dropdown-date-divider"></div>
          <BlInputDate
            class="bl-input-dropdown-date bl-input-dropdown-sub-input"
            :input="endDateInput"
            :data-id="id"
            @changed="inputHandler"
            disabledManualInput
          />
        </div>
      </div>

      <div
        v-else-if="tab.kind.includes('multi')"
        :data-id="id"
        class="flex flex-col gap-2 px-2 py-2"
      >
        <div :data-id="id" class="relative flex items-center px-2">
          <input
            :data-id="id"
            :id="`search_field_${tab.name}`"
            type="text"
            v-model="query"
            placeholder="Search ..."
            class="flex-shrink-0 w-full px-4 py-2 text-sm border rounded outline-none border-grey-200 focus:border-light-purple"
          />
          <BlIcon :data-id="id" icon="search" class="absolute w-6 right-4" />
        </div>
        <div
          v-if="filteredOptions.length === 0"
          :data-id="id"
          class="relative flex items-center justify-center px-4 py-2"
        >
          <div v-if="fetchLoading" :data-id="id" class="blum-loader-sm"></div>
          <BlTextBody2 v-else :data-id="id" text="Option not found" />
        </div>
        <div v-else :data-id="id" class="px-2 bl-input-dropdown-body">
          <div
            v-for="option in filteredOptions"
            class="bl-input-dropdown-option"
            :key="option.value || option"
            :data-id="id"
            @click="onChangeValue(option)"
            :title="option.label || option.name || option"
          >
            <BlInputCheck
              class="flex-shrink-0 pointer-events-none"
              :checked="
                value[tabIndex] &&
                  value[tabIndex].findIndex(
                    a => (a.value || a) === (option.value || option)
                  ) > -1
              "
              :data-id="id"
            />
            <div
              v-if="option.prefixColor"
              :data-id="id"
              class="flex-shrink-0 w-4 h-4 rounded-full"
              :style="{ backgroundColor: option.prefixColor }"
            ></div>
            <BlChip
              v-if="option.chip"
              :data-id="id"
              :id="id"
              :chip="option.value"
              dense
            />
            <div
              v-else-if="option.backgroundColor && option.color"
              class="truncate bl-input-dropdown-option-tag"
              :data-id="id"
              :style="{
                backgroundColor: option.backgroundColor,
                color: option.color
              }"
            >
              {{ option.label || option.name || option }}
            </div>
            <div
              v-else
              class="bl-input-dropdown-option-name line-clamp-2"
              :data-id="id"
              :title="option.label || option.name || option"
            >
              {{ option.label || option.name || option }}
            </div>
          </div>
          <BlInputButton
            v-if="isShowMoreOption"
            :dataId="id"
            class="mt-2 hover:bg-hover-state"
            type="plain"
            label="See more ..."
            dense
            :loading="fetchLoading"
            @clicked="onMoreOption"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import moment from "moment";
import debounce from "lodash/debounce";

import BlTextSubtitle2 from "./BlTextSubtitle2.vue";
import BlTextBody1 from "./BlTextBody1.vue";
import BlTextBody2 from "./BlTextBody2.vue";
import BlBubbleCount from "./BlBubbleCount.vue";
import BlIcon from "./BlIcon.vue";
import BlChip from "./BlChip.vue";
import BlInputButton from "./BlInputButton.vue";
import BlInputDate from "./BlInputDate.vue";
import BlInputCheck from "./BlInputCheck.vue";
import BlInputRadio from "./BlInputRadio.vue";
import BlInputSearch from "./BlInputSearch.vue";

export default {
  name: "BlInputFilter",
  components: {
    BlTextSubtitle2,
    BlTextBody1,
    BlTextBody2,
    BlIcon,
    BlChip,
    BlInputCheck,
    BlInputButton,
    BlInputDate,
    BlBubbleCount,
    BlInputRadio,
    BlInputSearch
  },
  props: {
    activeLabel: { type: String, default: "Filter by" },
    title: { type: String, default: "Filter by" },
    defaultValue: { type: Array, required: false, default: null },
    input: {
      disabled: { type: Boolean, required: false },
      model: { type: Object, required: true },
      tabs: { type: Object, required: true }
      /**
       * @tabs {
       *  name: string,
       *  kind: 'date' | 'date-custom' | 'multi' | 'multi-tag' (chip | color&backgroundColor),
       *  options?: string[] | {
       *    label: string,
       *    name: string,
       *    value: string,
       *    chip?: boolean,
       *    color?: string,
       *    backgroundColor?: string,
       *  },
       *  url: string,
       *  fetchPage: 1,
       *  dataKey: string,
       *  countKey: string,
       *  optionKeys: { value: string, label: string }
       *  }
       * }[]
       */
    }
  },
  data() {
    return {
      opened: false,
      value: [],
      menuTabs: [],
      tab: { name: "tab", kind: "" },
      fetchLoading: false,
      isShowMoreOption: false,
      query: "",
      searchInput: { model: "", placeholder: "Search", id: "" },
      //
      date: "custom",
      startDateInput: { model: "", disabled: false, id: "" },
      endDateInput: { model: "", disabled: false, id: "" }
    };
  },
  computed: {
    id() {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      let combination = "";
      for (let i = 0; i < 6; i++) {
        combination += chars[Math.round(Math.random() * 35)];
      }
      return `bl-input-filter-${combination}`;
    },
    filteredCount() {
      const val = [...this.value];
      let count = 0;
      val.forEach(i => {
        if (Array.isArray(i)) {
          count += i.length;
        } else if (typeof i === "object") {
          if (i.start_date || i.end_date) count += 1;
        }
      });
      return count;
    },
    tabIndex() {
      return this.menuTabs.findIndex(i => i.name === this.tab.name);
    },
    startDate() {
      if (this.date === "all_time") return "";
      return this.startDateInput.model;
    },
    endDate() {
      if (this.date === "all_time") return "";
      return this.endDateInput.model;
    },
    filteredOptions() {
      const query = this.query;
      const options = [...this.tab.options];

      if (!query) return options;
      return options.filter(i => {
        const lowerKey = query.toLowerCase();
        const label = i.label || i.name || i.value;
        const lowerLabel = label.toLowerCase();
        return lowerLabel.includes(lowerKey);
      });
    }
  },
  watch: {
    input: {
      deep: true,
      immediate: true,
      handler(val) {
        this.value = val.model;
        this.menuTabs = val.tabs;
      }
    },
    opened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandler);
        }, 10);
      } else {
        this.onSubmit();
      }
    },
    query(val) {
      this.fetchLoading = true;
      this.onSearch(val);
    },
    //
    date(val) {
      if (val === "custom") {
        this.startDateInput.disabled = false;
        this.endDateInput.disabled = false;
      } else {
        this.startDateInput.disabled = true;
        this.endDateInput.disabled = true;
        let num = -6;
        if (val === "last_week") num = -6;
        if (val === "last_month") num = -29;
        if (val === "last_year") num = -364;

        if (val === "all_time") {
          this.startDateInput.model = "";
          this.endDateInput.model = "";
        } else {
          this.startDateInput.model = moment()
            .add(num, "days")
            .format("YYYY-MM-DD");
          this.endDateInput.model = moment().format("YYYY-MM-DD");
        }
      }

      const model = this.value;

      if (val === "all_time") {
        model[this.tabIndex] = {
          start_date: "",
          end_date: ""
        };
      } else {
        model[this.tabIndex] = {
          start_date: this.startDate,
          end_date: this.endDate
        };
      }

      this.value = model;
    },
    startDate(val) {
      const model = JSON.parse(JSON.stringify(this.value));

      if (this.date === "all_time") {
        model[this.tabIndex] = {
          start_date: "",
          end_date: ""
        };
      } else {
        model[this.tabIndex] = {
          start_date: val,
          end_date: this.endDate
        };
      }

      this.value = model;
    },
    endDate(val) {
      const model = JSON.parse(JSON.stringify(this.value));

      if (this.date === "all_time") {
        model[this.tabIndex] = {
          start_date: "",
          end_date: ""
        };
      } else {
        model[this.tabIndex] = {
          start_date: this.startDate,
          end_date: val
        };
      }

      this.value = model;
    }
  },
  mounted() {
    this.value = this.input.model;
    this.menuTabs = this.input.tabs;

    if (this.input.tabs.length) {
      this.onChangeTab(this.input.tabs[0]);
    }
  },
  methods: {
    ...mapActions(["actionGet"]),
    removeDuplicates(arr, key) {
      const seen = new Set();
      return arr.filter(item => {
        const val = item[key];
        if (seen.has(val)) {
          return false;
        }
        seen.add(val);
        return true;
      });
    },
    //
    async onFetchOptions() {
      const url = this.tab.url;
      const query = this.query;
      const page = this.tab.fetchPage;
      const dataKey = this.tab.dataKey;
      const countKey = this.tab.countKey;
      const keys = this.tab.optionKeys;
      const options = [...this.tab.options];

      const params = { url: `${url}${query}` };
      params.url += `&per_page=${50}`;
      if (query) params.url += `&page=${1}`;
      else params.url += `&page=${page}`;

      this.fetchLoading = true;
      const { success, data } = await this.actionGet(params);
      this.fetchLoading = false;
      if (!success) return;
      if (!dataKey) return;

      const incomingArr = data[dataKey].map(i => {
        const opt = {
          value: i[keys.value],
          label: i[keys.label]
        };
        if (keys.prefixColor) {
          opt.prefixColor = i[keys.prefixColor] || keys.prefixColor;
        }
        if (keys.color) {
          opt.color = i[keys.color] || keys.color;
        }
        if (keys.backgroundColor) {
          opt.backgroundColor = i[keys.backgroundColor] || keys.backgroundColor;
        }
        return opt;
      });

      const lastArr = [...options, ...incomingArr];
      const uniqueArr = this.removeDuplicates(lastArr, "value");

      this.isShowMoreOption = true;
      if (uniqueArr.length >= data[countKey]) {
        this.isShowMoreOption = false;
      }

      this.tab.options = uniqueArr;
      this.menuTabs[this.tabIndex].options = uniqueArr;
    },
    onMoreOption() {
      const newPage = this.tab.fetchPage + 1;

      this.tab.fetchPage = newPage;
      this.menuTabs[this.tabIndex].fetchPage = newPage;

      this.onFetchOptions();
    },
    //
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    getTabFilteredCount(tab, idx) {
      let count = 0;
      if (this.value[idx]) {
        const val = this.value[idx];
        if (tab.kind && tab.kind.includes("date")) {
          if (val.start_date || val.end_date) count = 1;
        } else {
          count = val.length;
        }
      }
      return count;
    },
    onChangeTab(val) {
      this.tab = val;
      this.tab.fetchPage = 1;
      this.menuTabs[this.tabIndex].fetchPage = 1;

      if (val.kind && val.kind.includes("date")) {
        const model = this.value[this.tabIndex];
        this.startDateInput = {
          model: model.start_date,
          disabled: false,
          id: this.id
        };
        this.endDateInput = {
          model: model.end_date,
          disabled: false,
          id: this.id
        };
      }

      if (this.query) this.query = "";
      else if (val.url) this.onFetchOptions();
    },
    onSearch: debounce(function(e = "") {
      if (this.tab.url) {
        this.onFetchOptions();
      } else {
        this.fetchLoading = false;
      }
    }, 1000),
    onChangeValue(val) {
      const model = JSON.parse(JSON.stringify(this.value));

      if (this.tab.kind !== "date") {
        if (!model[this.tabIndex]) {
          model[this.tabIndex] = [];
        }
        const index = model[this.tabIndex].findIndex(
          a => (a.value || a) === (val.value || val)
        );
        if (index > -1) {
          model[this.tabIndex].splice(index, 1);
        } else {
          model[this.tabIndex].push(val);
        }
        this.value = model;
      }
    },
    onClearAll() {
      this.date = "custom";
      if (this.defaultValue) {
        this.value = this.defaultValue;
        this.menuTabs.forEach((tab, idx) => {
          if (tab.kind.includes("date")) {
            this.startDateInput = {
              id: this.id,
              model: this.defaultValue[idx].start_date,
              disabled: !this.defaultValue[idx].start_date
            };
            this.endDateInput = {
              id: this.id,
              model: this.defaultValue[idx].end_date,
              disabled: !this.defaultValue[idx].end_date
            };
          }
        });
      } else {
        this.value = this.menuTabs.map(tab => {
          if (tab.kind.includes("date")) {
            let disabled = false;
            if (tab.all_time) {
              disabled = true;
              this.date = "all_time";
            }
            this.startDateInput = { id: this.id, model: "", disabled };
            this.endDateInput = { id: this.id, model: "", disabled };
            return { start_date: "", end_date: "" };
          }
          return [];
        });
      }
      this.onSubmit();
    },
    onSubmit() {
      this.$emit("changed", { input: this.input, value: this.value });
    },
    //
    closeHandler(e) {
      let close = true;
      if (e.target && e.target.dataset && e.target.dataset.id) {
        const id = e.target.dataset.id || "";
        if (id === this.id) close = false;
      }
      if (close) {
        window.removeEventListener("click", this.closeHandler);
        this.opened = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.bl-input-component:not(.bl-input-dropdown-sub-input) {
  position: relative;
  height: 2.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-radius: 5px;
  padding: 0 3rem 0 1rem;
  &.bl-input-component-dense {
    height: 1.875rem;
  }
  > .bl-input-label {
    pointer-events: none;
    position: relative;
    padding: 0 0 0 0;
    box-sizing: border-box;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: calc(11 / 7);
    color: var(--font-secondary-color);
    letter-spacing: 0.5px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  > .bl-input {
    z-index: -1;
    pointer-events: none;
    position: absolute;
    width: auto;
    padding: 0 1rem 0 0.5rem;
    opacity: 0;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: calc(11 / 7);
    letter-spacing: 0.5px;
    box-sizing: border-box;
    display: flex;
    flex: 1;
  }
  > .bl-input-tags {
    cursor: pointer;
    z-index: 2;
    position: relative;
    width: 100%;
    height: 2.5rem;
    padding: 0 2rem 0 0.5rem;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    overflow-x: auto;
  }
  > .bl-input-border {
    cursor: pointer;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 2.5rem;
    border-radius: 5px;
    box-sizing: border-box;
    border: 1px solid var(--border-color);
    &.bl-input-border-dense {
      height: 1.875rem;
    }
  }
  > .bl-input-icon {
    cursor: pointer;
    position: absolute;
    right: 1rem;
    top: 0.75rem;
    width: 1rem;
    height: 1rem;
    &.bl-input-icon-dense {
      top: 0.4375rem;
    }
  }
  > .bl-input-dropdown {
    z-index: 90;
    position: absolute;
    top: 100%;
    left: 0;
    margin-top: 0.5rem;
    min-width: 23.75rem;
    width: 100%;
    background: var(--background-depth-one-color);
    border-radius: 5px;
    border: 1px solid var(--border-color);
    box-shadow: 0 0.5rem 1rem rgba(100, 100, 100, 0.05);
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    .bl-input-dropdown-header {
      position: relative;
      width: 100%;
      padding: 0.5rem 1rem 0 1rem;
      box-sizing: border-box;
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      align-items: center;
      .bl-input-dropdown-menu {
        position: relative;
        left: -1rem;
        width: calc(100% + 2rem);
        margin-top: 0.5rem;
        flex-shrink: 0;
        overflow-x: auto;
        &::-webkit-scrollbar {
          width: 0.5rem;
          height: 0.5rem;
        }
        &::-webkit-scrollbar-thumb {
          border-radius: 0.5rem;
          background-color: #d9d9d9;
        }
        .bl-input-dropdown-tabs {
          border-bottom: 1px solid var(--border-color);
          display: flex;
          .bl-input-dropdown-tab {
            cursor: pointer;
            position: relative;
            padding: 0 1rem 0.5rem 1rem;
            box-sizing: border-box;
            display: flex;
            flex-shrink: 0;
            align-items: center;
            gap: 0.5rem;
            &.bl-input-dropdown-tab-active {
              &::before {
                content: "";
                position: absolute;
                bottom: 0;
                left: 0;
                width: 100%;
                height: 0.25rem;
                background: var(--primary-color);
              }
            }
          }
        }
      }
    }
    .bl-input-dropdown-body {
      width: 100%;
      max-height: 16rem;
      display: flex;
      flex-direction: column;
      overflow-y: auto;
      .bl-input-dropdown-option {
        cursor: pointer;
        position: relative;
        width: 100%;
        height: 2.5rem;
        display: flex;
        flex-shrink: 0;
        gap: 0.75rem;
        align-items: center;
        .bl-input-dropdown-option-name {
          pointer-events: none;
          position: relative;
          font-size: 0.75rem;
          font-weight: 400;
          line-height: calc(5 / 3);
          letter-spacing: 0.5px;
        }
        .bl-input-dropdown-option-tag {
          pointer-events: none;
          position: relative;
          max-width: 100%;
          height: 1.5rem;
          padding: 0.125rem 0.5rem;
          // border-radius: 0.75rem;
          border-radius: 5px;
          box-sizing: border-box;
          font-size: 0.75rem;
          font-weight: 400;
          line-height: calc(5 / 3);
          letter-spacing: 0.5px;
        }
      }
      .bl-input-dropdown-date-wrapper {
        position: relative;
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        .bl-input-dropdown-date {
          position: relative;
          width: calc((100% - 1.25rem) / 2);
        }
        .bl-input-dropdown-date-divider {
          position: relative;
          width: 0.75rem;
          height: 0.125rem;
          background: var(--font-secondary-color);
        }
      }
    }
  }
  &.bl-input-component-active {
    > .bl-input-border {
      border-color: var(--primary-color);
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/BlInputFilter.vue